import { ModelID } from '@/types';

export type RealtimeChannelKind = 'public' | 'private' | 'presence';

export type RealtimeConnectionState =
  | 'initialized'
  | 'connecting'
  | 'connected'
  | 'unavailable'
  | 'failed'
  | 'disconnected'
  | 'reconnecting';

export type PresenceMember = {
  id: ModelID;
} & Record<string, unknown>;

export type PresenceMembersMap = Record<string, PresenceMember>;

export type RealtimeEventHandler<TPayload = unknown> = (
  payload: TPayload,
  eventName: string,
) => void;

export type PresenceMembersHandler = (members: PresenceMembersMap) => void;

type RealtimeEventBinding = {
  callbacks: Map<string, RealtimeEventHandler>;
  boundHandler: (payload: unknown) => void;
};

type RealtimeWhisperBinding = {
  callbacks: Map<string, RealtimeEventHandler>;
  boundHandler: (payload: unknown) => void;
};

type RealtimePresenceState = {
  members: PresenceMembersMap;
  subscribers: Map<string, PresenceMembersHandler>;
  lifecycleBound: boolean;
};

type RealtimeChannelEntry = {
  key: string;
  kind: RealtimeChannelKind;
  channelName: string;
  channel: any;
  refCount: number;
  eventBindings: Map<string, RealtimeEventBinding>;
  whisperBindings: Map<string, RealtimeWhisperBinding>;
  presence?: RealtimePresenceState;
  lastEventName: string | null;
  lastEventAt: string | null;
  lastError: string | null;
};

export type RealtimeChannelDiagnostics = {
  key: string;
  kind: RealtimeChannelKind;
  channelName: string;
  refCount: number;
  eventBindings: string[];
  hasPresence: boolean;
  memberCount: number;
  lastEventName: string | null;
  lastEventAt: string | null;
  lastError: string | null;
};

export type RealtimeDiagnostics = {
  connectionState: RealtimeConnectionState;
  connectionEpoch: number;
  totalChannels: number;
  channels: RealtimeChannelDiagnostics[];
  updatedAt: string;
};

type SubscribeToEventsOptions<TPayload = unknown> = {
  kind: RealtimeChannelKind;
  channelName: string;
  events: string[];
  handler: RealtimeEventHandler<TPayload>;
};

type SubscribeToPresenceOptions = {
  channelName: string;
  onMembersChanged: PresenceMembersHandler;
};

type SubscribeToPresenceWhisperOptions<TPayload = unknown> = {
  channelName: string;
  whisperName: string;
  handler: RealtimeEventHandler<TPayload>;
};

type RealtimeManagerOptions = {
  isDev?: boolean;
};

export class RealtimeManager {
  private readonly isDev: boolean;

  private readonly channels = new Map<string, RealtimeChannelEntry>();

  private readonly diagnosticsSubscribers = new Set<(snapshot: RealtimeDiagnostics) => void>();

  private echoInstance: any | null = null;

  private connectionState: RealtimeConnectionState = 'disconnected';

  private connectionEpoch = 0;

  private idCounter = 0;

  private connectionListenersBound = false;

  constructor(options: RealtimeManagerOptions = {}) {
    this.isDev = options.isDev ?? false;
  }

  public setEcho(echoInstance: any): void {
    if (!echoInstance) {
      return;
    }

    this.echoInstance = echoInstance;
    this.attachConnectionListeners();
    this.emitDiagnostics();
  }

  public subscribeToEvents<TPayload = unknown>({
    kind,
    channelName,
    events,
    handler,
  }: SubscribeToEventsOptions<TPayload>): () => void {
    if (!channelName.trim()) {
      return () => undefined;
    }

    const normalizedEvents = Array.from(
      new Set(events.map((event) => event.trim()).filter(Boolean)),
    );
    if (normalizedEvents.length === 0) {
      return () => undefined;
    }

    const entry = this.ensureChannelEntry(kind, channelName);
    if (!entry) {
      return () => undefined;
    }

    entry.refCount += 1;
    const callbackId = this.nextId('event');

    for (const eventName of normalizedEvents) {
      const existingBinding = entry.eventBindings.get(eventName);

      if (existingBinding) {
        existingBinding.callbacks.set(callbackId, handler as RealtimeEventHandler);
        continue;
      }

      const newBinding: RealtimeEventBinding = {
        callbacks: new Map([[callbackId, handler as RealtimeEventHandler]]),
        boundHandler: (payload: unknown) => {
          const currentEntry = this.channels.get(entry.key);
          if (!currentEntry) {
            return;
          }

          const currentBinding = currentEntry?.eventBindings.get(eventName);
          if (!currentBinding) {
            return;
          }

          currentEntry.lastEventName = eventName;
          currentEntry.lastEventAt = new Date().toISOString();

          for (const callback of currentBinding.callbacks.values()) {
            try {
              callback(payload, eventName);
            } catch (error) {
              currentEntry.lastError = this.toErrorMessage(error);
              if (this.isDev) {
                console.error('[realtime] event handler failed', {
                  channel: currentEntry.channelName,
                  eventName,
                  error,
                });
              }
            }
          }

          this.emitDiagnostics();
        },
      };

      entry.eventBindings.set(eventName, newBinding);
      this.bindEvent(entry, eventName, newBinding.boundHandler);
    }

    this.emitDiagnostics();

    let released = false;

    return () => {
      if (released) {
        return;
      }
      released = true;

      const currentEntry = this.channels.get(entry.key);
      if (!currentEntry) {
        return;
      }

      for (const eventName of normalizedEvents) {
        const currentBinding = currentEntry.eventBindings.get(eventName);
        if (!currentBinding) {
          continue;
        }

        currentBinding.callbacks.delete(callbackId);

        if (currentBinding.callbacks.size === 0) {
          this.unbindEvent(currentEntry, eventName, currentBinding.boundHandler);
          currentEntry.eventBindings.delete(eventName);
        }
      }

      currentEntry.refCount = Math.max(0, currentEntry.refCount - 1);
      this.releaseEntryIfUnused(currentEntry);
      this.emitDiagnostics();
    };
  }

  public subscribeToPresence({
    channelName,
    onMembersChanged,
  }: SubscribeToPresenceOptions): () => void {
    if (!channelName.trim()) {
      return () => undefined;
    }

    const entry = this.ensureChannelEntry('presence', channelName);
    if (!entry || !entry.presence) {
      return () => undefined;
    }

    entry.refCount += 1;

    const subscriberId = this.nextId('presence');
    entry.presence.subscribers.set(subscriberId, onMembersChanged);

    onMembersChanged({ ...entry.presence.members });
    this.emitDiagnostics();

    let released = false;

    return () => {
      if (released) {
        return;
      }
      released = true;

      const currentEntry = this.channels.get(entry.key);
      if (!currentEntry || !currentEntry.presence) {
        return;
      }

      currentEntry.presence.subscribers.delete(subscriberId);
      currentEntry.refCount = Math.max(0, currentEntry.refCount - 1);

      this.releaseEntryIfUnused(currentEntry);
      this.emitDiagnostics();
    };
  }

  public subscribeToPresenceWhisper<TPayload = unknown>({
    channelName,
    whisperName,
    handler,
  }: SubscribeToPresenceWhisperOptions<TPayload>): () => void {
    if (!channelName.trim() || !whisperName.trim()) {
      return () => undefined;
    }

    const entry = this.ensureChannelEntry('presence', channelName);
    if (!entry) {
      return () => undefined;
    }

    entry.refCount += 1;

    const callbackId = this.nextId('whisper');
    const existingBinding = entry.whisperBindings.get(whisperName);

    if (existingBinding) {
      existingBinding.callbacks.set(callbackId, handler as RealtimeEventHandler);
      this.emitDiagnostics();
    } else {
      const newBinding: RealtimeWhisperBinding = {
        callbacks: new Map([[callbackId, handler as RealtimeEventHandler]]),
        boundHandler: (payload: unknown) => {
          const currentEntry = this.channels.get(entry.key);
          if (!currentEntry) {
            return;
          }

          const currentBinding = currentEntry.whisperBindings.get(whisperName);
          if (!currentBinding) {
            return;
          }

          currentEntry.lastEventName = `whisper:${whisperName}`;
          currentEntry.lastEventAt = new Date().toISOString();

          for (const callback of currentBinding.callbacks.values()) {
            try {
              callback(payload, whisperName);
            } catch (error) {
              currentEntry.lastError = this.toErrorMessage(error);
              if (this.isDev) {
                console.error('[realtime] whisper handler failed', {
                  channel: currentEntry.channelName,
                  whisperName,
                  error,
                });
              }
            }
          }

          this.emitDiagnostics();
        },
      };

      entry.whisperBindings.set(whisperName, newBinding);
      this.bindPresenceWhisper(entry, whisperName, newBinding.boundHandler);
      this.emitDiagnostics();
    }

    let released = false;

    return () => {
      if (released) {
        return;
      }
      released = true;

      const currentEntry = this.channels.get(entry.key);
      if (!currentEntry) {
        return;
      }

      const currentBinding = currentEntry.whisperBindings.get(whisperName);
      if (currentBinding) {
        currentBinding.callbacks.delete(callbackId);

        if (currentBinding.callbacks.size === 0) {
          this.unbindPresenceWhisper(currentEntry, whisperName, currentBinding.boundHandler);
          currentEntry.whisperBindings.delete(whisperName);
        }
      }

      currentEntry.refCount = Math.max(0, currentEntry.refCount - 1);
      this.releaseEntryIfUnused(currentEntry);
      this.emitDiagnostics();
    };
  }

  public sendPresenceWhisper(channelName: string, whisperName: string, payload: unknown): void {
    if (!channelName.trim() || !whisperName.trim()) {
      return;
    }

    const entry = this.ensureChannelEntry('presence', channelName);
    if (!entry) {
      return;
    }

    try {
      if (typeof entry.channel?.whisper === 'function') {
        entry.channel.whisper(whisperName, payload);
      }
    } catch (error) {
      entry.lastError = this.toErrorMessage(error);
      this.emitDiagnostics();
    }
  }

  public mergePresenceMember(channelName: string, memberPatch: Record<string, unknown>): void {
    const entry = this.channels.get(this.channelKey('presence', channelName));
    if (!entry || !entry.presence) {
      return;
    }

    const member = this.normalizePresenceMember(memberPatch);
    if (!member) {
      return;
    }

    const memberKey = member.id.toString();
    const previous = entry.presence.members[memberKey] ?? { id: member.id };

    entry.presence.members = {
      ...entry.presence.members,
      [memberKey]: {
        ...previous,
        ...member,
      },
    };

    this.notifyPresenceSubscribers(entry);
    this.emitDiagnostics();
  }

  public getPresenceMembers(channelName: string): PresenceMembersMap {
    const entry = this.channels.get(this.channelKey('presence', channelName));

    if (!entry || !entry.presence) {
      return {};
    }

    return { ...entry.presence.members };
  }

  public leaveChannel(kind: RealtimeChannelKind, channelName: string): void {
    const entry = this.channels.get(this.channelKey(kind, channelName));
    if (!entry) {
      return;
    }

    this.teardownChannel(entry);
    this.channels.delete(entry.key);
    this.emitDiagnostics();
  }

  public subscribeToDiagnostics(listener: (snapshot: RealtimeDiagnostics) => void): () => void {
    this.diagnosticsSubscribers.add(listener);
    listener(this.getDiagnosticsSnapshot());

    return () => {
      this.diagnosticsSubscribers.delete(listener);
    };
  }

  public getDiagnosticsSnapshot(): RealtimeDiagnostics {
    const channels = Array.from(this.channels.values()).map<RealtimeChannelDiagnostics>(
      (entry) => ({
        key: entry.key,
        kind: entry.kind,
        channelName: entry.channelName,
        refCount: entry.refCount,
        eventBindings: [
          ...Array.from(entry.eventBindings.keys()),
          ...Array.from(entry.whisperBindings.keys()).map((name) => `whisper:${name}`),
        ],
        hasPresence: Boolean(entry.presence),
        memberCount: entry.presence ? Object.keys(entry.presence.members).length : 0,
        lastEventName: entry.lastEventName,
        lastEventAt: entry.lastEventAt,
        lastError: entry.lastError,
      }),
    );

    return {
      connectionState: this.connectionState,
      connectionEpoch: this.connectionEpoch,
      totalChannels: channels.length,
      channels,
      updatedAt: new Date().toISOString(),
    };
  }

  private nextId(prefix: string): string {
    this.idCounter += 1;
    return `${prefix}-${this.idCounter}`;
  }

  private channelKey(kind: RealtimeChannelKind, channelName: string): string {
    return `${kind}:${channelName}`;
  }

  private ensureChannelEntry(
    kind: RealtimeChannelKind,
    channelName: string,
  ): RealtimeChannelEntry | null {
    const key = this.channelKey(kind, channelName);
    const existing = this.channels.get(key);
    if (existing) {
      return existing;
    }

    if (!this.echoInstance) {
      if (this.isDev) {
        console.warn('[realtime] ignored subscription before Echo was configured', {
          kind,
          channelName,
        });
      }
      return null;
    }

    const entry: RealtimeChannelEntry = {
      key,
      kind,
      channelName,
      channel: this.createChannel(kind, channelName),
      refCount: 0,
      eventBindings: new Map(),
      whisperBindings: new Map(),
      lastEventName: null,
      lastEventAt: null,
      lastError: null,
      presence:
        kind === 'presence'
          ? {
              members: {},
              subscribers: new Map(),
              lifecycleBound: false,
            }
          : undefined,
    };

    this.channels.set(key, entry);

    if (kind === 'presence') {
      this.bindPresenceLifecycle(entry);
    }

    return entry;
  }

  private createChannel(kind: RealtimeChannelKind, channelName: string): any {
    if (!this.echoInstance) {
      throw new Error('Echo instance is not configured.');
    }

    if (kind === 'public') {
      return this.echoInstance.channel(channelName);
    }

    if (kind === 'private') {
      return this.echoInstance.private(channelName);
    }

    return this.echoInstance.join(channelName);
  }

  private bindEvent(
    entry: RealtimeChannelEntry,
    eventName: string,
    callback: (payload: unknown) => void,
  ): void {
    try {
      if (typeof entry.channel?.listen === 'function') {
        entry.channel.listen(eventName, callback);
        return;
      }

      if (typeof entry.channel?.bind === 'function') {
        entry.channel.bind(eventName, callback);
      }
    } catch (error) {
      entry.lastError = this.toErrorMessage(error);
      this.emitDiagnostics();
    }
  }

  private unbindEvent(
    entry: RealtimeChannelEntry,
    eventName: string,
    callback: (payload: unknown) => void,
  ): void {
    try {
      if (typeof entry.channel?.stopListening === 'function') {
        entry.channel.stopListening(eventName);
        return;
      }

      if (typeof entry.channel?.unbind === 'function') {
        entry.channel.unbind(eventName, callback);
      }
    } catch (error) {
      entry.lastError = this.toErrorMessage(error);
    }
  }

  private bindPresenceWhisper(
    entry: RealtimeChannelEntry,
    whisperName: string,
    callback: (payload: unknown) => void,
  ): void {
    try {
      if (typeof entry.channel?.listenForWhisper === 'function') {
        entry.channel.listenForWhisper(whisperName, callback);
      }
    } catch (error) {
      entry.lastError = this.toErrorMessage(error);
      this.emitDiagnostics();
    }
  }

  private unbindPresenceWhisper(
    entry: RealtimeChannelEntry,
    whisperName: string,
    callback: (payload: unknown) => void,
  ): void {
    try {
      if (typeof entry.channel?.stopListeningForWhisper === 'function') {
        entry.channel.stopListeningForWhisper(whisperName);
        return;
      }

      if (typeof entry.channel?.unbind === 'function') {
        entry.channel.unbind(whisperName, callback);
      }
    } catch (error) {
      entry.lastError = this.toErrorMessage(error);
    }
  }

  private bindPresenceLifecycle(entry: RealtimeChannelEntry): void {
    if (!entry.presence || entry.presence.lifecycleBound) {
      return;
    }

    const presence = entry.presence;

    if (typeof entry.channel?.here === 'function') {
      entry.channel.here((members: unknown[]) => {
        const nextMembers: PresenceMembersMap = {};

        for (const rawMember of members ?? []) {
          const normalized = this.normalizePresenceMember(rawMember);
          if (!normalized) {
            continue;
          }

          nextMembers[normalized.id.toString()] = normalized;
        }

        presence.members = nextMembers;
        this.notifyPresenceSubscribers(entry);
        this.emitDiagnostics();
      });
    }

    if (typeof entry.channel?.joining === 'function') {
      entry.channel.joining((member: unknown) => {
        const normalized = this.normalizePresenceMember(member);
        if (!normalized) {
          return;
        }

        const memberKey = normalized.id.toString();

        presence.members = {
          ...presence.members,
          [memberKey]: normalized,
        };

        this.notifyPresenceSubscribers(entry);
        this.emitDiagnostics();
      });
    }

    if (typeof entry.channel?.leaving === 'function') {
      entry.channel.leaving((member: unknown) => {
        const normalized = this.normalizePresenceMember(member);
        if (!normalized) {
          return;
        }

        const memberKey = normalized.id.toString();
        const nextMembers = { ...presence.members };
        delete nextMembers[memberKey];

        presence.members = nextMembers;

        this.notifyPresenceSubscribers(entry);
        this.emitDiagnostics();
      });
    }

    if (typeof entry.channel?.error === 'function') {
      entry.channel.error((error: unknown) => {
        entry.lastError = this.toErrorMessage(error);
        this.emitDiagnostics();
      });
    }

    presence.lifecycleBound = true;
  }

  private notifyPresenceSubscribers(entry: RealtimeChannelEntry): void {
    if (!entry.presence) {
      return;
    }

    const snapshot = { ...entry.presence.members };

    for (const subscriber of entry.presence.subscribers.values()) {
      subscriber(snapshot);
    }
  }

  private normalizePresenceMember(rawMember: unknown): PresenceMember | null {
    if (!rawMember || typeof rawMember !== 'object') {
      return null;
    }

    const memberCandidate = rawMember as Record<string, unknown>;
    const id = memberCandidate.id;

    if (typeof id !== 'string' && typeof id !== 'number') {
      return null;
    }

    return {
      ...memberCandidate,
      id,
    } as PresenceMember;
  }

  private releaseEntryIfUnused(entry: RealtimeChannelEntry): void {
    const hasPresenceSubscribers = Boolean(entry.presence && entry.presence.subscribers.size > 0);

    if (
      entry.refCount > 0 ||
      entry.eventBindings.size > 0 ||
      entry.whisperBindings.size > 0 ||
      hasPresenceSubscribers
    ) {
      return;
    }

    this.teardownChannel(entry);
    this.channels.delete(entry.key);
  }

  private teardownChannel(entry: RealtimeChannelEntry): void {
    if (!this.echoInstance) {
      return;
    }

    for (const [eventName, binding] of entry.eventBindings.entries()) {
      this.unbindEvent(entry, eventName, binding.boundHandler);
    }

    for (const [whisperName, binding] of entry.whisperBindings.entries()) {
      this.unbindPresenceWhisper(entry, whisperName, binding.boundHandler);
    }

    entry.eventBindings.clear();
    entry.whisperBindings.clear();

    try {
      this.echoInstance.leaveChannel(this.resolveEchoChannelName(entry.kind, entry.channelName));
    } catch (error) {
      entry.lastError = this.toErrorMessage(error);
    }
  }

  private resolveEchoChannelName(kind: RealtimeChannelKind, channelName: string): string {
    if (kind === 'public') {
      return channelName;
    }

    if (kind === 'private') {
      return `private-${channelName}`;
    }

    return `presence-${channelName}`;
  }

  private attachConnectionListeners(): void {
    if (this.connectionListenersBound || !this.echoInstance) {
      return;
    }

    const pusherConnection = this.echoInstance?.connector?.pusher?.connection;

    if (typeof pusherConnection?.bind !== 'function') {
      return;
    }

    this.connectionListenersBound = true;

    this.connectionState =
      (pusherConnection.state as RealtimeConnectionState | undefined) ?? this.connectionState;

    pusherConnection.bind('state_change', (states: any) => {
      const previous = states?.previous as RealtimeConnectionState | undefined;
      const current = states?.current as RealtimeConnectionState | undefined;

      if (current) {
        this.connectionState = current;
      }

      if (current === 'connected' && previous !== 'connected') {
        this.connectionEpoch += 1;
        this.rebuildAllChannels();
      }

      this.emitDiagnostics();
    });

    pusherConnection.bind('error', (error: unknown) => {
      const message = this.toErrorMessage(error);

      for (const entry of this.channels.values()) {
        entry.lastError = message;
      }

      this.emitDiagnostics();
    });
  }

  private rebuildAllChannels(): void {
    const entries = Array.from(this.channels.values());

    for (const entry of entries) {
      if (!this.echoInstance) {
        return;
      }

      try {
        this.echoInstance.leaveChannel(this.resolveEchoChannelName(entry.kind, entry.channelName));
      } catch {
        // We intentionally ignore leave failures and continue rebuilding.
      }

      entry.channel = this.createChannel(entry.kind, entry.channelName);

      if (entry.presence) {
        entry.presence.lifecycleBound = false;
        entry.presence.members = {};
        this.bindPresenceLifecycle(entry);
        this.notifyPresenceSubscribers(entry);
      }

      for (const [eventName, binding] of entry.eventBindings.entries()) {
        this.bindEvent(entry, eventName, binding.boundHandler);
      }

      for (const [whisperName, binding] of entry.whisperBindings.entries()) {
        this.bindPresenceWhisper(entry, whisperName, binding.boundHandler);
      }
    }
  }

  private toErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'Unknown realtime error';
  }

  private emitDiagnostics(): void {
    if (this.diagnosticsSubscribers.size === 0) {
      return;
    }

    const snapshot = this.getDiagnosticsSnapshot();

    for (const subscriber of this.diagnosticsSubscribers) {
      subscriber(snapshot);
    }
  }
}
